import { Shield, Scale, FileText, HeartPulse, Star } from "lucide-react";
import { lazy, Suspense } from "react";
import LorsLogoBanner from "./LorsLogoBanner";
import shawnPhoto from "@/assets/shawn-h.webp";
import rubyPhoto from "@/assets/ruby-headshot.webp";
import type { LorsCopy } from "./translations";

// Lazy-load the form so its dependencies (Supabase client, Toast, Checkbox,
// react-router navigate) don't block the hero text from painting. The form
// is below the fold on mobile and isn't needed for LCP.
const LorsForm = lazy(() => import("./LorsForm"));

const ICONS = [Scale, FileText, HeartPulse, Shield];

const LorsHero = ({
  copy,
  redirectTo,
  directionsUrl,
  headlineSizeClass = "text-3xl md:text-[52px] leading-snug md:leading-[72px]",
  subheadLines,
  bulletsMontserrat = false,
  formKey,
}: {
  copy: LorsCopy;
  redirectTo: string;
  directionsUrl?: string;
  headlineSizeClass?: string;
  subheadLines?: string[];
  bulletsMontserrat?: boolean;
  formKey: "may13" | "may20" | "may29" | "june2" | "june25" | "july16" | "july24" | "aug4" | "aug12" | "sep10" | "sep22" | "sep23" | "oct15" | "oct22" | "oct28" | "nov10" | "nov19" | "dec9" | "dec17" | "odw";
}) => {
  return (
    <section className="bg-background">
      <LorsLogoBanner />
      <div
        className="container max-w-[1320px] mx-auto px-6 py-8 pt-10"
      >
        <div className="text-center mb-6">
          <span
            className="lors-eyebrow inline-block text-xs md:text-sm tracking-widest uppercase font-semibold px-4 py-2 rounded-full"
            style={{
              backgroundColor: "hsl(199 21% 86%)",
              color: "hsl(210 24% 20%)",
            }}
          >
            {copy.eyebrow}
          </span>
        </div>

        <h1 className={`lors-headline ${headlineSizeClass} mb-4 text-center max-w-5xl mx-auto`}>
          {(() => {
            const marker = copy.headlineBreakBefore;
            const idx = marker ? copy.headline.lastIndexOf(marker) : -1;
            if (idx === -1) return copy.headline;
            const before = copy.headline.slice(0, idx).trimEnd();
            const after = copy.headline.slice(idx);
            return (
              <>
                {before}
                <br className="md:hidden" />
                <span> {after}</span>
              </>
            );
          })()}
        </h1>

        <div className="flex items-center justify-center gap-4 mb-14 max-w-5xl mx-auto px-4">
          <img
            src={rubyPhoto}
            alt="Ruby Steinbrecher"
            width={224}
            height={224}
            decoding="async"
            loading="eager"
            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0 border-2"
            style={{ borderColor: "hsl(199 21% 86%)" }}
          />
          <p
            className={`text-base md:text-lg leading-7 ${
              subheadLines && subheadLines.length > 0 ? "text-center font-medium" : "lors-subhead text-left font-light"
            }`}
            style={{
              color: "hsl(210 24% 35%)",
              ...(subheadLines && subheadLines.length > 0
                ? { fontFamily: "'Montserrat', system-ui, sans-serif" }
                : {}),
            }}
          >
            {subheadLines && subheadLines.length > 0
              ? subheadLines.map((line, i) => (
                  <span key={i} className="block" style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
                    {line}
                  </span>
                ))
              : copy.subhead}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
          {/* Left column - What you'll learn */}
          <div className="flex-1">
            <h2 className="lors-headline text-2xl md:text-3xl leading-snug md:leading-[2.75rem] mb-6">
              {copy.learnTitle}
            </h2>
            <div className="space-y-5">
              {copy.learnItems.map((item, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                      style={{ backgroundColor: "hsl(199 21% 86%)" }}
                    >
                      <Icon className="w-4 h-4" style={{ color: "hsl(210 24% 20%)" }} />
                    </div>
                    <p
                      className="text-base leading-7"
                      style={
                        bulletsMontserrat
                          ? { fontFamily: "'Montserrat', system-ui, sans-serif" }
                          : undefined
                      }
                    >
                      {item}
                    </p>
                  </div>
                );
              })}
            </div>

            <div
              className="flex items-start gap-4 mt-8 pt-6 border-t"
              style={{ borderColor: "hsl(199 21% 86%)" }}
            >
              <img
                src={shawnPhoto}
                alt="Shawn H."
                width={160}
                height={160}
                loading="lazy"
                decoding="async"
                className="w-16 h-16 rounded-full object-cover flex-shrink-0"
              />
              <div>
                <div className="flex gap-0.5 mb-2" role="img" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4"
                      style={{ color: "hsl(48 100% 50%)", fill: "hsl(48 100% 50%)" }}
                    />
                  ))}
                </div>
                <p
                  className="italic leading-snug text-sm md:text-base"
                  style={{
                    color: "hsl(210 24% 20%)",
                    fontFamily: "'Montserrat', system-ui, sans-serif",
                  }}
                >
                  "{copy.quote}"
                  <span
                    className="block not-italic mt-2 text-sm font-semibold"
                    style={{
                      color: "hsl(210 24% 35%)",
                      fontFamily: "'Montserrat', system-ui, sans-serif",
                    }}
                  >
                    — {copy.quoteAttribution}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Right column - form */}
          <div id="lors-form" className="w-full lg:w-1/2 flex-shrink-0 scroll-mt-4 min-h-[600px]">
            <Suspense fallback={<div className="bg-card rounded-xl border border-border min-h-[600px]" />}>
              <LorsForm copy={copy} redirectTo={redirectTo} directionsUrl={directionsUrl} formKey={formKey} />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LorsHero;
